/**
 * ArTableOptimizerControls
 *
 * The commission optimizer trigger and popover for ArTable (Tables v2): sizes the
 * available markdown, takes a target price drop, runs the solver and clears what
 * it wrote.
 *
 * The optimizer's row set is resolved HERE (SYD-2429), from `uiState.selection`
 * and `uiState.selectableRowIds` snapshots at render time, and travels into
 * `useArTableOptimizer` as an argument — the hook reads no selection. Opening the
 * popover captures that scope into `formState` beside the totals it produced, and
 * the run and the clear pass the captured value, so what the figures describe is
 * what the write touches. The popover therefore does not recompute while open;
 * it cannot need to, because the trigger is `click` and any tick lands outside
 * the popover and closes it.
 */

import { FC, memo, useCallback, useContext, useMemo, useState } from 'react';
import { Button, Descriptions, InputNumber, Popover, Space, Tooltip, Typography, theme } from 'antd';
import { ClearOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import { MessageContext } from '@/providers/MessageContext';
import { formDeriveCopy } from '@/services/formDerives';
import type { OrderByOption } from '@/store/Store.types';
import { CAPABILITY_BOM_OPTIMIZER } from '#types/Client.Parameters.types';
import type { ArTableId } from '#types/System.Artifacts.types';
import { ArTableUIStateContext } from '../context';
import { resolveSelectionScope } from '../selection';
import { resolveOptimizerRowScope, type ArTableOptimizerRowScope } from './resolveOptimizerRowScope';
import { useArTableOptimizer } from './useArTableOptimizer';

const { Text } = Typography;

const MIN_REDUCTION = 100;

const HIDDEN_SELECTION_REASON =
  'The selected rows are hidden by a filter, split or view. Show them or clear the selection.';

type ReductionFormState = {
  /** The rows the figures below were computed from, and the rows a run will write. */
  rowScope: ArTableOptimizerRowScope;
  totals: {
    currentTotalPrice: number;
    currentTotalCommission: number;
    unlockedLineCount: number;
    totalLineCount: number;
  };
  maxOBaseReduction: {
    amount: number;
    percentage: number;
  };
  maxCutPriceReduction: {
    amount: number;
    percentage: number;
  };
};

const formatCurrency = (value: number): string =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const rowsToOptimize = ({ totals, rowScope }: ReductionFormState): string => {
  const { unlockedLineCount: unlocked, totalLineCount: total } = totals;
  const rows = total === 1 ? 'row' : 'rows';
  if (rowScope.allRows) {
    return unlocked === total
      ? `All ${total} ${rows} in this table`
      : `${unlocked} unlocked of ${total} ${rows} in this table`;
  }
  return unlocked === total
    ? `${total} selected ${rows}`
    : `${unlocked} unlocked of ${total} selected ${rows}`;
};

const scopeRowNoun = (rowScope: ArTableOptimizerRowScope, count: number): string =>
  `${rowScope.allRows ? '' : 'selected '}row${count === 1 ? '' : 's'}`;

/**
 * Why Run and Clear Opt are dead. An empty considered set is reported as such —
 * an empty table, or a ticked bundle header — because "every row is locked" names
 * a lock that is not there and asks for an unlock that cannot be made.
 */
const noUnlockedReason = ({ totals, rowScope }: ReductionFormState): string | undefined => {
  if (totals.unlockedLineCount > 0) return undefined;
  if (totals.totalLineCount === 0) return 'No rows to optimize.';
  return rowScope.allRows
    ? 'Every row is locked. Unlock a row to optimize it.'
    : 'Every selected row is locked. Unlock a row to optimize it.';
};

type ArTableOptimizerControlsProps = {
  tableId: ArTableId;
  parentPath: string;
  rowsOrderBy: OrderByOption;
  isPaginated: boolean;
  disabled?: boolean;
};

const ArTableOptimizerControls: FC<ArTableOptimizerControlsProps> = ({
  tableId,
  parentPath,
  rowsOrderBy,
  isPaginated,
  disabled = false,
}) => {
  const { token } = theme.useToken();
  const { messageApi, handleFirestoreWriteError } = useContext(MessageContext);
  const uiState = useContext(ArTableUIStateContext)!;
  const selectableSnap = useSnapshot(uiState.selectableRowIds);
  const selectionSnap = useSnapshot(uiState.selection);
  const optimizer = useArTableOptimizer({
    tableId,
    parentPath,
    rowsOrderBy,
    isPaginated,
  });

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [targetReduction, setTargetReduction] = useState<number>(MIN_REDUCTION);
  const [formState, setFormState] = useState<ReductionFormState | null>(null);

  /** The rows the next open will size. Snapshot-derived, so it is what the user sees. */
  const rowScope = resolveOptimizerRowScope({
    selectedCount: selectionSnap.selectedCount,
    selection: resolveSelectionScope({
      selectableRowIds: selectableSnap,
      selectedRows: selectionSnap.selectedRows,
      scope: selectionSnap.scope,
    }),
  });
  /** Ticks exist but a filter, split or view hides every one of them. */
  const nothingActionable = rowScope.rowIds !== undefined && rowScope.rowIds.length === 0;

  const readiness = optimizer.getReadiness();

  const isDisabled = disabled || isOptimizing || !readiness.ready;

  const nothingUnlocked = formState !== null && formState.totals.unlockedLineCount === 0;
  const lockedReason = formState ? noUnlockedReason(formState) : undefined;

  const maxReductionAmount = useMemo(() => {
    const max = Math.floor(formState?.maxCutPriceReduction.amount ?? 0);
    return Math.max(0, max);
  }, [formState?.maxCutPriceReduction.amount]);

  const hasValidMax = maxReductionAmount >= MIN_REDUCTION;
  const currentTotalPrice = formState?.totals.currentTotalPrice ?? 0;

  const amountToPercentage = useCallback((amount: number): number => {
    if (currentTotalPrice <= 0) return 0;
    return (amount / currentTotalPrice) * 100;
  }, [currentTotalPrice]);

  const percentageToAmount = useCallback((percentage: number): number => {
    return (percentage / 100) * currentTotalPrice;
  }, [currentTotalPrice]);

  const currentPercentage = amountToPercentage(targetReduction);
  const minPercentage = amountToPercentage(MIN_REDUCTION);
  const maxPercentage = amountToPercentage(maxReductionAmount);

  const handleOpenModal = async () => {
    if (!readiness.ready) {
      messageApi.error({
        content: readiness.reasons.join('\n'),
        duration: 8,
        style: { whiteSpace: 'pre-line' },
      });
      return;
    }

    try {
      const summary = await optimizer.calculateSummary(rowScope);
      setFormState({
        rowScope,
        totals: {
          currentTotalPrice: summary.totals.totalPrice,
          currentTotalCommission: summary.totals.totalCommission,
          unlockedLineCount: summary.totals.unlockedLineCount,
          totalLineCount: summary.totals.totalLineCount,
        },
        maxOBaseReduction: summary.maxOBaseReduction,
        maxCutPriceReduction: summary.maxCutPriceReduction,
      });

      setTargetReduction(MIN_REDUCTION);
      setIsPopoverOpen(true);
    } catch (_error) {
      messageApi.error('Failed to load optimizer data. Please try again.');
    }
  };

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
  };

  const handleTargetReductionChange = (amount: number | null) => {
    if (amount === null) return;
    setTargetReduction(amount);
  };

  const handlePercentageChange = (percentage: number | null) => {
    if (percentage === null) return;
    const amount = Math.round(percentageToAmount(percentage));
    const clampedAmount = Math.max(MIN_REDUCTION, Math.min(amount, maxReductionAmount));
    setTargetReduction(clampedAmount);
  };

  const canRun =
    readiness.ready &&
    !disabled &&
    currentTotalPrice > 0 &&
    targetReduction >= MIN_REDUCTION &&
    hasValidMax &&
    targetReduction <= maxReductionAmount;

  const handleRunOptimizer = async () => {
    if (!canRun || !formState) return;

    setIsOptimizing(true);
    try {
      const runResult = await optimizer.runOptimizer({ targetReduction, rowScope: formState.rowScope });
      const backendMessage = runResult.result.modelMeta?.message || 'Optimization complete.';
      const failedMessage = runResult.failedRowsCount > 0
        ? `\n${runResult.failedRowsCount} row${runResult.failedRowsCount === 1 ? '' : 's'} failed to save.`
        : '';
      /** The run already opens with its own count, so only the reason sentence is added. */
      const derivedMessage = runResult.derivedCellsCount > 0
        ? `\n${formDeriveCopy.derivedCellsSkipped(runResult.derivedCellsCount)}`
        : '';
      const messageType = runResult.failedRowsCount > 0 || runResult.derivedCellsCount > 0
        ? 'warning'
        : 'success';
      messageApi[messageType]({
        content: `Optimized ${runResult.updatedRowsCount} ${scopeRowNoun(formState.rowScope, runResult.updatedRowsCount)}.\n${backendMessage}${failedMessage}${derivedMessage}`,
        duration: 8,
        style: { whiteSpace: 'pre-line' },
      });
      handleClosePopover();
    } catch (error) {
      if (error instanceof Error) {
        const code = (error as { code?: string }).code;
        const isFunctionsError = typeof code === 'string' && code.startsWith('functions/');

        if (isFunctionsError) {
          messageApi.error({
            content: error.message,
            duration: 8,
            style: { whiteSpace: 'pre-line' },
          });
        } else {
          const isUserMessage =
            error.message.startsWith('Cannot optimize:') ||
            error.message.startsWith('Invalid reduction:') ||
            error.message.startsWith('Optimization failed:') ||
            error.message === 'The optimizer timed out. Please let Syncretic know via the feedback button.';

          if (isUserMessage) {
            messageApi.error({
              content: error.message,
              duration: 8,
              style: { whiteSpace: 'pre-line' },
            });
          } else {
            handleFirestoreWriteError(error, '[ArTableOptimizerControls] run optimizer', {
              userMessage: 'Failed to apply optimizer results. Please try again.',
            });
          }
        }
        return;
      }
      messageApi.error('Optimization failed. Please try again.');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleClearOptimization = async () => {
    if (isDisabled || nothingUnlocked || !formState) return;

    setIsOptimizing(true);
    try {
      const result = await optimizer.clearMarkdown(formState.rowScope);
      const clearedRows = `${result.cleared} ${scopeRowNoun(formState.rowScope, result.cleared)}`;
      const failedMessage = result.failed > 0
        ? ` ${result.failed} row${result.failed === 1 ? '' : 's'} failed to save.`
        : '';
      if (result.failed > 0) {
        messageApi.warning(`Cleared markdown for ${clearedRows}.${failedMessage}`);
      } else if (result.derived > 0) {
        messageApi.warning(
          `Cleared markdown for ${clearedRows}. ${formDeriveCopy.derivedCellsSkipped(result.derived)}`,
        );
      } else if (result.skipped > 0) {
        messageApi.info(`Cleared markdown for ${clearedRows}. ${result.skipped} rows skipped.`);
      } else {
        messageApi.success(`Cleared markdown for ${clearedRows}.`);
      }
    } catch (error) {
      handleFirestoreWriteError(error, '[ArTableOptimizerControls] clear markdown', {
        userMessage: 'Failed to clear optimization. Please try again.',
      });
    } finally {
      setIsOptimizing(false);
    }
  };

  if (!CAPABILITY_BOM_OPTIMIZER) {
    return null;
  }

  return (
    <Tooltip title={nothingActionable ? HIDDEN_SELECTION_REASON : undefined}>
      <span
        style={{ display: 'inline-flex' }}
        aria-label={nothingActionable ? 'artable-optimize-hidden-selection-reason' : undefined}
      >
        <Popover
          title="Commission Optimizer"
          styles={{ content: { maxWidth: 450 } }}
          content={
            <FlexCol>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Rows to Optimize">
                  <Text>{formState ? rowsToOptimize(formState) : ''}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Bid Price of Unlocked Rows">
                  <Text>{formatCurrency(formState?.totals.currentTotalPrice ?? 0)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Commission of Unlocked Rows">
                  <Text>{formatCurrency(formState?.totals.currentTotalCommission ?? 0)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="If Drop to Base Price">
                  <Text>
                    {formatCurrency(formState?.maxOBaseReduction.amount ?? 0)} ({(formState?.maxOBaseReduction.percentage ?? 0).toFixed(1)}%)
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="If Drop to Min Price (Max)">
                  <Text>
                    {formatCurrency(formState?.maxCutPriceReduction.amount ?? 0)} ({(formState?.maxCutPriceReduction.percentage ?? 0).toFixed(1)}%)
                  </Text>
                </Descriptions.Item>
              </Descriptions>

              <FlexCol noGap>
                <Text>Target Price Drop</Text>
                <Space.Compact block>
                  <InputNumber
                    style={{ width: '50%' }}
                    prefix="$"
                    precision={0}
                    value={targetReduction}
                    onChange={handleTargetReductionChange}
                    min={MIN_REDUCTION}
                    max={hasValidMax ? maxReductionAmount : undefined}
                    step={100}
                    formatter={(value) => {
                      if (value === undefined || value === null) return '';
                      return Number(value).toLocaleString('en-US');
                    }}
                    parser={(value) => {
                      if (!value) return MIN_REDUCTION;
                      return Number(value.replace(/,/g, ''));
                    }}
                    aria-label="artable-optimizer-target-amount"
                  />
                  <InputNumber
                    style={{ width: '50%' }}
                    suffix="%"
                    precision={2}
                    value={currentPercentage}
                    onChange={handlePercentageChange}
                    min={minPercentage}
                    max={hasValidMax ? maxPercentage : undefined}
                    step={0.1}
                    aria-label="artable-optimizer-target-percent"
                  />
                </Space.Compact>
              </FlexCol>

              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                Min: {formatCurrency(MIN_REDUCTION)} ({minPercentage.toFixed(2)}%)
                {hasValidMax && (
                  <> · Max: {formatCurrency(maxReductionAmount)} ({maxPercentage.toFixed(1)}%)</>
                )}
              </Text>

              <FlexBox stretch>
                <Button onClick={handleClosePopover} aria-label="artable-optimizer-cancel">
                  Cancel
                </Button>
                <Tooltip title={lockedReason}>
                  <span
                    style={{ display: 'inline-flex' }}
                    aria-label="artable-clear-optimization-locked-reason"
                  >
                    <Button
                      icon={<ClearOutlined />}
                      onClick={() => void handleClearOptimization()}
                      disabled={isDisabled || nothingUnlocked}
                      loading={isOptimizing}
                      danger
                      aria-label="artable-clear-optimization-button"
                      type="link"
                    >
                      Clear Opt
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={lockedReason}>
                  <span
                    style={{ display: 'inline-flex' }}
                    aria-label="artable-optimizer-run-locked-reason"
                  >
                    <Button
                      type="primary"
                      onClick={() => void handleRunOptimizer()}
                      disabled={!canRun}
                      loading={isOptimizing}
                      aria-label="artable-optimizer-run"
                    >
                      Run Optimizer
                    </Button>
                  </span>
                </Tooltip>
              </FlexBox>
            </FlexCol>
          }
          open={isPopoverOpen}
          onOpenChange={(open) => {
            if (open) void handleOpenModal();
            else handleClosePopover();
          }}
          trigger="click"
        >
          <Button
            type="text"
            size="small"
            icon={<ThunderboltOutlined />}
            disabled={isDisabled || nothingActionable}
            loading={isOptimizing}
            aria-label="artable-optimize-button"
          >
            Optimize
          </Button>
        </Popover>
      </span>
    </Tooltip>
  );
};

const MemoizedArTableOptimizerControls = memo(ArTableOptimizerControls);
export { MemoizedArTableOptimizerControls as ArTableOptimizerControls };
